import { doc, onSnapshot, updateDoc, increment, addDoc, collection, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

/**
 * Game Service
 * Handles real-time game state synchronization and action submission.
 */

const NEXUS_PATH = 'artifacts/whispers-of-harmony/public/data/nexuses';
const CHALLENGES_PATH = 'artifacts/whispers-of-harmony/public/data/challenges';

// Throttle uploads to save costs and prevent write exhaustion
const UPDATE_THROTTLE_MS = 500; 
let pendingBalanceChange = 0;
let lastUpdate = 0;
let throttleTimer = null;

let pendingState = null;
let stateThrottleTimer = null;

/**
 * Subscribe to a specific showdown game document.
 * Supports both Nexus-wide events (Tug-of-War) and specific Challenge instances.
 * @param {string} id - nexusId OR challengeId
 * @param {function} callback 
 * @param {boolean} isChallenge - If true, treats id as challengeId
 * @returns {function} Unsubscribe function
 */
export const subscribeToGame = (id, callback, isChallenge = false) => {
  if (!id) return () => {};

  let gameDocRef;
  if (isChallenge) {
     gameDocRef = doc(db, `${CHALLENGES_PATH}/${id}/game_state/tug_of_war`);
  } else {
     gameDocRef = doc(db, `${NEXUS_PATH}/${id}/dreamweave/showdown`);
  }

  const unsubscribe = onSnapshot(gameDocRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ ...docSnap.data(), gameId: docSnap.id, id });
    } else {
      // Initialize if missing (for ephemeral challenges)
      if (isChallenge) {
          // We don't await this, just fire and forget to init state
          setDoc(gameDocRef, { balance: 0, lastActive: new Date() }, { merge: true });
          callback({ balance: 0, gameId: id, id });
      } else {
          callback(null);
      }
    }
  }, (error) => {
    console.error("Game subscription error:", error);
  });

  return unsubscribe;
};

/**
 * Sends a user game action (aggregated).
 * Uses client-side throttling to minimize Firestore writes.
 * @param {string} id - nexusId OR challengeId
 * @param {number} power 
 * @param {boolean} isChallenge 
 */
export const sendGameAction = (id, power, isChallenge = false) => {
  if (!id) return;

  pendingBalanceChange += power;

  const now = Date.now();
  if (now - lastUpdate > UPDATE_THROTTLE_MS) {
    flushGameAction(id, isChallenge);
  } else {
    if (throttleTimer) clearTimeout(throttleTimer);
    throttleTimer = setTimeout(() => flushGameAction(id, isChallenge), UPDATE_THROTTLE_MS);
  }
};

const flushGameAction = async (id, isChallenge) => {
  if (pendingBalanceChange === 0) return;

  const amount = pendingBalanceChange;
  pendingBalanceChange = 0;
  lastUpdate = Date.now();

  let gameDocRef;
  if (isChallenge) {
     gameDocRef = doc(db, `${CHALLENGES_PATH}/${id}/game_state/tug_of_war`);
  } else {
     gameDocRef = doc(db, `${NEXUS_PATH}/${id}/dreamweave/showdown`);
  }
  
  try {
    await updateDoc(gameDocRef, {
      balance: increment(amount),
      lastActive: new Date()
    });
  } catch (error) {
    console.error("Failed to send game action:", error);
  }
};

/**
 * Sends player state (position, etc.) for Arcade games.
 * Throttled to ~500ms.
 */
export const sendPlayerState = (challengeId, role, state) => {
    if (!challengeId || !role) return;
    pendingState = state;

    if (!stateThrottleTimer) {
        stateThrottleTimer = setTimeout(() => {
            flushPlayerState(challengeId, role);
        }, UPDATE_THROTTLE_MS);
    }
};

const flushPlayerState = async (challengeId, role) => {
    if (!pendingState) return;
    const stateToSend = pendingState;
    pendingState = null;
    stateThrottleTimer = null;

    const stateRef = doc(db, `${CHALLENGES_PATH}/${challengeId}/game_state/${role}`);
    try {
        await setDoc(stateRef, { ...stateToSend, timestamp: new Date() }, { merge: true });
    } catch (error) {
        console.error("Failed to sync player state:", error);
    }
};

/**
 * Subscribe to opponent's state for Arcade games.
 */
export const subscribeToPlayerState = (challengeId, role, callback) => {
    if (!challengeId || !role) return () => {};
    const stateRef = doc(db, `${CHALLENGES_PATH}/${challengeId}/game_state/${role}`);
    return onSnapshot(stateRef, (snap) => {
        if (snap.exists()) callback(snap.data());
    });
};


/**
 * Submits the full game log for AI training.
 * @param {string} nexusId 
 * @param {string} gameId 
 * @param {Array} logs 
 */
export const submitGameLog = async (nexusId, gameId, logs) => {
  if (!logs || logs.length === 0) return;

  // Use nexusId for organizing logs even if it's a challenge
  // Fallback to 'global' if no nexusId
  const targetNexusId = nexusId || 'global';

  console.log(`[DreamWeave] Uploading ${logs.length} training vectors for Game ${gameId} in Nexus ${targetNexusId}`);
  
  const logsCollectionRef = collection(db, `${NEXUS_PATH}/${targetNexusId}/dreamweave/showdown_logs`);
  
  try {
    await addDoc(logsCollectionRef, {
      gameId,
      timestamp: new Date(),
      logs, // Be careful with size limits (1MB). For very long games, batch this.
      vectorCount: logs.length
    });
    return true;
  } catch (error) {
    console.error("Failed to submit game log:", error);
    return false;
  }
};